import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Location } from "history";
import { confirmLeaveWithUnsavedChanges } from "./confirmations";

// 🔴 `history.block`, not `<Prompt>`: `<Prompt>` can only ask through `window.confirm`.
export function useUnsavedChangesGuard(hasUnsavedChanges: boolean) {
  const history = useHistory();
  const [pendingLocation, setPendingLocation] = useState<Location>();

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return undefined;
    }

    const unblock = history.block((location) => {
      setPendingLocation(location);

      return false;
    });

    return unblock;
  }, [hasUnsavedChanges, history]);

  useEffect(() => {
    if (!pendingLocation) {
      return;
    }

    confirmLeaveWithUnsavedChanges().then((leave) => {
      setPendingLocation(undefined);
      if (!leave) {
        return;
      }

      // 🔴 Unblock first, or the guard stops the navigation it just approved.
      history.block(() => undefined);
      history.push(pendingLocation);
    });
  }, [history, pendingLocation]);

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return undefined;
    }

    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", warn);

    return () => window.removeEventListener("beforeunload", warn);
  }, [hasUnsavedChanges]);
}
